import React, { useEffect, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import { Button, message, Tag } from "antd";
import dayjs from "dayjs";
import {
  PlusCircleOutlined,
  FileOutlined,
  DeleteOutlined,
  FolderOpenOutlined,
  FileTextOutlined,
  CheckCircleFilled,
  MinusOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import Table from "../../../../../Common/Table";
import SelectReadingsModal from "../../../../../Common/SelectReadingsModal";
import { useParams, useSearchParams } from "react-router";
import {
  ReadingGetParams,
  ReadingTableRow,
  useReadingTableGet,
  useReadingUpdate,
} from "../../../../../../hooks/api/readings";
import { SortOrder } from "antd/es/table/interface";
import { RequestData } from "@ant-design/pro-components";
import { getSorterParams } from "../../../../../../utils";
import { TYPE_COLORS } from "../../../../ContentLibrary/ReadingsTab";
import DeleteReadingButton from "./actions";
import { useVaadTabs } from "../context";

interface ReadingsTabProps {
  activeTab?: string;
}

const TAG_COLORS: Record<
  string,
  { bg: string; border: string; color: string }
> = {
  gratitude: { bg: "#e6fffb", border: "#87e8de", color: "#13c2c2" },
  kindness: { bg: "#fff7e6", border: "#ffd591", color: "#d46b08" },
  joy: { bg: "#f6ffed", border: "#b7eb8f", color: "#389e0d" },
  patience: { bg: "#fff2f0", border: "#ffccc7", color: "#cf1322" },
};

const getTagStyle = (tag: string) => {
  const normalized = tag.toLowerCase();
  return (
    TAG_COLORS[normalized] || {
      bg: "#f5f5f5",
      border: "#d9d9d9",
      color: "#666",
    }
  );
};

function ToolBar({ onCreate }: { onCreate: () => void }) {
  return [
    <Button
      key="create"
      icon={<FolderOpenOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        onCreate();
      }}
    >
      Add from Library
    </Button>,
  ];
}

export default function ReadingsTab({
  activeTab,
}: ReadingsTabProps): React.ReactNode | null {
  const { id } = useParams();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const [current, setCurrent] = useState<number>(1);
  const { t, i18n } = useTranslation();
  const readingTableGet = useReadingTableGet();
  const [selectReadingsOpen, setSelectReadingsOpen] = useState(false);
  const readingUpdate = useReadingUpdate();
  const { middahOptions } = useVaadTabs();

  useEffect(() => {
    if (activeTab === "readings") {
      setTableKey(Date.now());
    }
  }, [activeTab]);

  const handleAddReadings = async (selectedReadings: any[]) => {
    if (!id) {
      message.error("Vaad ID is missing");
      return;
    }

    let successCount = 0;
    let errorCount = 0;

    for (const reading of selectedReadings) {
      const readingId = reading.id; // ← берём id из объекта

      console.log("Updating reading:", readingId);

      const result = await readingUpdate.fetch({ vaad: { id: id } }, readingId);

      if (result instanceof Error || (result as any)?.isAxiosError) {
        console.error("Failed:", readingId, result);
        errorCount++;
      } else {
        console.log("Success:", readingId);
        successCount++;
      }
    }

    if (errorCount === 0) {
      message.success(`${successCount} readings added successfully`);
    } else {
      message.warning(`${successCount} added, ${errorCount} failed`);
    }

    setTableKey(Date.now());
  };

  const columns: ProColumns<ReadingTableRow>[] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => (
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <FileTextOutlined />
          {record.fileLink ? (
            <a
              href={record.fileLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "underline", color: "#262626" }}
            >
              {record.name}
            </a>
          ) : (
            <span style={{ color: "#262626" }}>{record.name}</span>
          )}
        </span>
      ),
    },
    {
      title: "Middah",
      dataIndex: "middahIds",
      key: "middahIds",
      sorter: true,
      valueType: "select",
      hideInTable: true,
      valueEnum: Object.fromEntries(
        middahOptions.map((m) => [m.value, { text: m.label }]),
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      valueType: "select",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => (
        <Tag
          variant="outlined"
          color={TYPE_COLORS[record.type.toLowerCase()] || "default"}
          style={{ textTransform: "uppercase", fontSize: 12 }}
        >
          {record.type}
        </Tag>
      ),
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      valueType: "select",
      hideInSearch: true,
      render: (_, record) => {
        const allTags = record.middahs || [];
        const visibleTags = allTags.slice(0, 3);
        const hiddenCount = allTags.length - 3;

        return (
          <span
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            {visibleTags.map((tag) => (
              <Tag color="#722ED1" variant="outlined" key={tag.id}>
                {tag.name}
              </Tag>
            ))}
            {hiddenCount > 0 && <span>+{hiddenCount}</span>}
          </span>
        );
      },
    },
    {
      title: "Global",
      dataIndex: "globalContent",
      key: "globalContent",
      align: "center",
      hideInSearch: true,
      render: (_, record) =>
        record.globalContent ? (
          <CheckCircleFilled style={{ color: "#52C41A", fontSize: 16 }} />
        ) : (
          <MinusOutlined style={{ color: "#BFBFBF" }} />
        ),
    },
    {
      title: "Upload Date",
      dataIndex: "createdAt",
      key: "createdAt",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => {
        const date = dayjs(record.createdAt);
        return date.isValid() ? date.format("MMMM D, YYYY") : "-";
      },
    },
    {
      title: "Associated Meeting",
      dataIndex: "associatedMeeting",
      key: "associatedMeeting",
      sorter: true,
      hideInSearch: true,
      render: (_, record) => <div>{record.meeting?.name || "-"}</div>,
    },
  ];

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number;
      current?: number;
      keyword?: string;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<ReadingTableRow>>> => {
    const size = pageSize || 10;
    const requestedPage = current || 1;
    const newParams: ReadingGetParams = {
      vaadId: id,
      page: requestedPage,
      pageSize: size,
      orderByColumn: "createdAt",
      orderBy: "DESC",
      ...args,
      ...getSorterParams(sorter),
    };

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    const fetchPage = (
      page: number,
    ): Promise<Partial<RequestData<ReadingTableRow>>> =>
      readingTableGet.fetch({ ...newParams, page }).then((response) => {
        if (!response) {
          return { data: [], success: false, total: 0 };
        }

        const { data, total } = response;
        const lastPage = Math.max(1, Math.ceil((total || 0) / size));

        // The last item on the page was removed: fall back to the last page
        // that still has data instead of showing an empty list.
        if ((!data || data.length === 0) && total > 0 && page > lastPage) {
          setCurrent(lastPage);
          return fetchPage(lastPage);
        }

        setCurrent(page);

        return {
          data: data || [],
          success: true,
          total,
        };
      });

    return fetchPage(requestedPage);
  };

  return (
    <>
      <Table<ReadingTableRow>
        key={`readings-${tableKey}-${i18n.language}`}
        resetKey={() => setTableKey(Date.now())}
        className="tableCustomPadding"
        headerTitle={t("pages.readings.title")}
        request={tableRequest}
        pagination={{
          current,
          onChange: (page) => setCurrent(page),
        }}
        toolBarRender={() => [
          <ToolBar
            key="toolbar"
            onCreate={() => setSelectReadingsOpen(true)}
          />,
        ]}
        columns={columns}
        actions={[
          [
            DeleteReadingButton,
            {
              setTableKey,
            },
          ],
        ]}
        search={{
          style: {
            // display: "none",
          },
        }}
        options={{
          search: {
            name: "search",
            placeholder: "Search by reading name",
            style: { width: 280 },
          },
        }}
        columnsState={{
          persistenceKey: "pro-table-readings",
          persistenceType: "localStorage",
        }}
      />
      <SelectReadingsModal
        open={selectReadingsOpen}
        onClose={() => setSelectReadingsOpen(false)}
        onAdd={(ids) => {
          handleAddReadings(ids as []);
        }}
      />
    </>
  );
}
